/**
 * The Wallet's numbers (docs/redesign-2026-11/design/payments.html, section 00 and PW1–PW4) as pure functions: week
 * names and dates, the weekly chart's geometry, the partial-withdrawal amount and what may stop a withdrawal.
 * Amounts are integer rao (1 α = 10^9 rao).
 */
import { DEFAULT_APP_CONFIG, type MyPayouts, type MyRewardsTotals, type RewardWeek, type RewardWeekStatement, type Wallet } from '@robotensor/api'

export const RAO = 1_000_000_000
/** Horizon's public site: the same origin as this app since 2026-11-02 (deploy/gateway/Caddyfile). */
export const CONTRIBUTE = 'https://horizon.robotensor.ai'

/** ISO week number (weeks start Monday, UTC): week 44 of 2026 is Mon 26 Oct – Sun 1 Nov. */
export function isoWeek(iso: string | Date): number {
  const d = new Date(typeof iso === 'string' ? iso : iso.getTime())
  const day = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()))
  const dow = day.getUTCDay() || 7
  day.setUTCDate(day.getUTCDate() + 4 - dow)
  const yearStart = Date.UTC(day.getUTCFullYear(), 0, 1)
  return Math.ceil(((day.getTime() - yearStart) / 86_400_000 + 1) / 7)
}

/** "Week 44": the service's ISO week number, else worked out from the start. */
export const weekName = (w: Pick<RewardWeek, 'starts_at' | 'week'>) => `Week ${w.week ?? isoWeek(w.starts_at)}`

/** The week's last day: `ends_at` is the exclusive end (Monday 00:00 UTC) or the last instant of Sunday. */
function lastDay(w: Pick<RewardWeek, 'ends_at'>): Date {
  return new Date(Date.parse(w.ends_at) - 1)
}

const day = (d: Date, opts: Intl.DateTimeFormatOptions) => d.toLocaleDateString('en-GB', { timeZone: 'UTC', ...opts })

/** "26 Oct–1 Nov", or "19–25 Oct" inside one month. */
export function weekRange(w: Pick<RewardWeek, 'starts_at' | 'ends_at'>): string {
  const a = new Date(w.starts_at)
  const b = lastDay(w)
  if (a.getUTCMonth() === b.getUTCMonth()) return `${a.getUTCDate()}–${day(b, { day: 'numeric', month: 'short' })}`
  return `${day(a, { day: 'numeric', month: 'short' })}–${day(b, { day: 'numeric', month: 'short' })}`
}

/** "Mon 26 Oct – Sun 1 Nov (UTC)". */
export function weekSpan(w: Pick<RewardWeek, 'starts_at' | 'ends_at'>): string {
  const f = (d: Date) => day(d, { weekday: 'short', day: 'numeric', month: 'short' }).replace(',', '')
  return `${f(new Date(w.starts_at))} – ${f(lastDay(w))} (UTC)`
}

/** "Sun 8 Nov": when a running week ends. */
export function weekEnds(w: Pick<RewardWeek, 'ends_at'>): string {
  return day(lastDay(w), { weekday: 'short', day: 'numeric', month: 'short' }).replace(',', '')
}

/** 292 → "2.92%". */
export function sharePct(bps: number): string {
  return `${(bps / 100).toFixed(2)}%`
}

/** Alpha with one decimal ("1,719.0") or none for whole totals ("2,219"). */
export function alpha(rao: number, decimals = 1): string {
  const v = rao / RAO
  return v.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
}

// ── Chart (G1) ───────────────────────────────────────────────────────────────────────────────────────────────

export interface BarPart {
  kind: 'sim' | 'ego' | 'other'
  y: number
  h: number
  value: number
}

export interface Bar {
  epoch: string
  x: number
  w: number
  label: string
  current: boolean
  total: number
  parts: BarPart[]
}

export interface ChartModel {
  bars: Bar[]
  ticks: { y: number; value: number }[]
  max: number
  /** The latest finished week, whose total is written above its bar. */
  marked: string | null
}

export const CHART = { width: 620, height: 190, left: 34, right: 612, top: 10, bottom: 168 } as const

/** The chart's frame at a given drawing width (the SVG is drawn 1:1 at its measured width, so text never scales). */
export function chartFrame(width: number = CHART.width) {
  return { ...CHART, width, right: Math.max(CHART.left + 40, width - 8) }
}

/** A round axis maximum (1, 2, 2.5, 5 × 10^n) at or above `v`. */
export function niceMax(v: number): number {
  if (!(v > 0)) return 1
  const p = 10 ** Math.floor(Math.log10(v))
  for (const m of [1, 2, 2.5, 5, 10]) if (m * p >= v) return m * p
  return 10 * p
}

/**
 * Stacked bars, oldest left: robot control at the bottom, recording on top, this week faded. `value` turns a week's
 * reward split into the unit shown (α, or $ at today's price).
 */
export function chartModel(weeks: readonly RewardWeek[], value: (rao: number) => number, width: number = CHART.width): ChartModel {
  const F = chartFrame(width)
  const list = [...weeks].sort((a, b) => Date.parse(a.starts_at) - Date.parse(b.starts_at))
  const split = (w: RewardWeek) => {
    const total = value(w.reward_rao ?? 0)
    const k = w.points_by_kind
    const pts = k.sim + k.ego + (k.other ?? 0)
    const sim = pts > 0 ? (total * k.sim) / pts : total
    const ego = pts > 0 ? (total * k.ego) / pts : 0
    return { total, sim, ego, other: Math.max(0, total - sim - ego) }
  }
  const max = niceMax(Math.max(0, ...list.map((w) => split(w).total)))
  const plotH = F.bottom - F.top
  const slot = (F.right - F.left) / Math.max(1, list.length)
  const bw = Math.min(36, slot * 0.62)
  const done = list.filter((w) => w.state !== 'counting')
  const marked = done.length ? done[done.length - 1]!.epoch_id : null
  const bars = list.map((w, i) => {
    const s = split(w)
    const x = F.left + slot * i + (slot - bw) / 2
    const hs = (s.sim / max) * plotH
    const he = (s.ego / max) * plotH
    const ho = (s.other / max) * plotH
    const current = w.state === 'counting'
    return {
      epoch: w.epoch_id,
      x,
      w: bw,
      label: current ? 'now' : `W${w.week ?? isoWeek(w.starts_at)}`,
      current,
      total: s.total,
      parts: [
        { kind: 'sim' as const, y: F.bottom - hs, h: hs, value: s.sim },
        { kind: 'ego' as const, y: F.bottom - hs - he, h: he, value: s.ego },
        { kind: 'other' as const, y: F.bottom - hs - he - ho, h: ho, value: s.other },
      ],
    }
  })
  const ticks = [0, max / 2, max].map((v) => ({ value: v, y: F.bottom - (v / max) * plotH }))
  return { bars, ticks, max, marked }
}

// ── Withdrawals (PW1, PW4) ───────────────────────────────────────────────────────────────────────────────────

/** "250", "250.5", "1,200" → rao; null for anything that isn't a positive amount. */
export function parseAlpha(text: string): number | null {
  const t = text.replace(/[,\s_α]/g, '')
  if (!/^\d*(\.\d{0,9})?$/.test(t) || t === '' || t === '.') return null
  const [whole = '0', frac = ''] = t.split('.')
  const rao = Number(whole) * RAO + Number(frac.padEnd(9, '0') || '0')
  return Number.isFinite(rao) ? rao : null
}

/** An amount field's text for rao, without trailing zeros ("250", "12.5"). */
export function alphaInput(rao: number): string {
  const whole = Math.floor(rao / RAO)
  const frac = String(rao % RAO).padStart(9, '0').replace(/0+$/, '')
  return frac ? `${whole}.${frac.slice(0, 4)}` : String(whole)
}

/** 25 % / 50 % / Max of what can be withdrawn now, rounded down to 0.1 α (Max is exact). */
export function shareOf(withdrawable: number, pct: number): number {
  return shareOfBps(withdrawable, pct * 100)
}

/** A withdraw shortcut (basis points, from the app config's `rules.withdraw_shortcuts_bps`): 10000 = everything. */
export function shareOfBps(withdrawable: number, bps: number): number {
  if (bps >= 10000) return withdrawable
  const step = RAO / 10
  return Math.floor((withdrawable * bps) / 10000 / step) * step
}

/** The minimum withdrawal: the balance's own, else the app config's rule (default 1 α). */
export function payoutMinimum(balance: { payout_min_rao?: number | null } | null | undefined, ruleRao: number = DEFAULT_APP_CONFIG.rules.payout_min_rao): number {
  return balance?.payout_min_rao || ruleRao || RAO
}

/**
 * What a withdrawal of `amount` sends to the wallet: the amount minus the network fee (the server takes it out of the
 * amount and freezes it in the signed document; `balance.network_fee_rao` is today's). Null without an amount.
 */
export function receiveOf(amount: number | null | undefined, feeRao: number | null | undefined): number | null {
  if (amount == null || !Number.isFinite(amount) || amount <= 0) return null
  return Math.max(0, amount - Math.max(0, feeRao ?? 0))
}

/** A network fee for people: "0.04 α" (up to four decimals, never rounded to nothing), or null for no fee. */
export function feeText(feeRao: number | null | undefined): string | null {
  if (!feeRao || feeRao <= 0) return null
  return `${feeRao < RAO / 10_000 ? '< 0.0001' : alphaInput(feeRao)} α`
}

export type AmountCheck = { ok: true } | { ok: false; reason: string }

/** The withdraw button says why it can't go: "Enter an amount", "Minimum 1 α", "More than you can withdraw now". */
export function checkAmount(amount: number | null, withdrawable: number, minimum: number): AmountCheck {
  if (amount === null || amount <= 0) return { ok: false, reason: 'Enter an amount' }
  if (amount < minimum) return { ok: false, reason: `Minimum ${alpha(minimum, minimum % RAO ? 1 : 0)} α` }
  if (amount > withdrawable) return { ok: false, reason: 'More than you can withdraw now' }
  return { ok: true }
}

export type WithdrawGate =
  | { kind: 'open' }
  | { kind: 'no_wallet' }
  | { kind: 'cooldown'; until: string }
  | { kind: 'vault_not_live' }
  | { kind: 'on_hold' }
  | { kind: 'below_minimum' }

/** What the balance card's Withdraw button does now (PW1 states), most serious first. */
export function withdrawGate(
  r: Pick<MyRewardsTotals, 'on_hold' | 'wallet_linked' | 'settlement' | 'balance'>,
  wallet: Pick<Wallet, 'address' | 'payable_at'> | undefined,
  now = Date.now(),
  rules: { settlement?: string | null; payout_min_rao?: number } = {},
): WithdrawGate {
  if (r.on_hold) return { kind: 'on_hold' }
  // the rewards' own settlement wins; the app config's feature fills in for an older rewards answer
  const settlement = r.settlement ?? rules.settlement
  if (settlement && settlement !== 'live') return { kind: 'vault_not_live' }
  if (!r.wallet_linked && !wallet?.address) return { kind: 'no_wallet' }
  if (wallet?.payable_at && Date.parse(wallet.payable_at) > now) return { kind: 'cooldown', until: wallet.payable_at }
  const b = r.balance
  if (!b || b.withdrawable_rao < payoutMinimum(b, rules.payout_min_rao)) return { kind: 'below_minimum' }
  return { kind: 'open' }
}

/** "Thu 5 Nov 14:00" in the viewer's time. */
export function whenShort(iso: string): string {
  const d = new Date(iso)
  return `${d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }).replace(',', '')} ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`
}

export interface WithdrawalLine {
  id: string
  at: string
  /** What reached (or is on its way to) the wallet, after the network fee. */
  amount_rao: number
  fee_rao: number
  /** arrived (on chain), sending (waiting for the transfer), counted (dry run), delayed (still waiting after a day),
   * failed (not sent: back in the balance with its fee). */
  status: 'arrived' | 'sending' | 'counted' | 'delayed' | 'failed'
  url: string | null
  automatic: boolean
}

/** The Withdrawals card: withdrawals and automatic payouts sent to the wallet, newest first. */
export function withdrawalLines(p: MyPayouts | undefined): WithdrawalLine[] {
  if (!p) return []
  return p.payouts
    .filter((r) => r.kind === 'withdrawal' || (r.kind === 'payout' && !!r.address && !!r.receipt))
    .map((r) => ({
      id: r.id,
      at: r.receipt?.at ?? r.created_at,
      amount_rao: r.amount_rao,
      fee_rao: r.fee_rao ?? 0,
      status: r.status === 'sent' ? ('arrived' as const) : r.status === 'failed' ? ('failed' as const) : r.status === 'delayed' ? ('delayed' as const) : p.settlement === 'dry-run' ? ('counted' as const) : ('sending' as const),
      url: r.receipt?.url ?? null,
      automatic: r.kind === 'payout',
    }))
    .sort((a, b) => Date.parse(b.at) - Date.parse(a.at))
}

/** The transfer receipt for a week: its statement's automatic payout, else the payout row for that week. */
export function weekReceipt(w: RewardWeekStatement, p: MyPayouts | undefined): string | null {
  if (w.transfer?.receipt?.url) return w.transfer.receipt.url
  const row = p?.payouts.find((r) => r.kind === 'payout' && r.period && Math.abs(Date.parse(r.period.starts_at) - Date.parse(w.starts_at)) < 86_400_000)
  return row?.receipt?.url ?? null
}
